import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { X, Loader2, FlaskConical, Plus, Beaker, RefreshCw, Sparkles, TrendingUp, Zap, Crosshair, Target, ChevronRight, Crown, XCircle } from "lucide-react";
import BrainLabBacktestModal from "./BrainLabBacktestModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getAccessCode() {
  try {
    const session = JSON.parse(localStorage.getItem("bc_access_session") || "null");
    return session?.code || null;
  } catch {
    return null;
  }
}

function authHeaders() {
  const code = getAccessCode();
  return code ? { "X-Access-Code": code } : {};
}

// Recognise auth/premium gating errors so we can show a friendly CTA
// instead of the raw backend detail like "Token mancante" / "Token scaduto".
function isAuthOrPremiumError(err) {
  const status = err?.response?.status;
  if (status === 401 || status === 402) return true;
  const detail = String(err?.response?.data?.detail || "").toLowerCase();
  return detail.includes("token") || detail.includes("premium") || detail.includes("loggato");
}

function extractError(err, fallback = "Scansione fallita") {
  if (isAuthOrPremiumError(err)) {
    return { type: "premium", message: null };
  }
  return { type: "error", message: err?.response?.data?.detail || fallback };
}

const C = {
  bg: "rgba(0,0,0,0.82)",
  panel: "#09090B",
  panelElev: "#141416",
  border: "rgba(255,255,255,0.08)",
  text: "#FFFFFF",
  muted: "#71717A",
  cyan: "#22D3EE",
  amber: "#F59E0B",
  violet: "#A78BFA",
  green: "#22FFA3",
  red: "#EF4444",
};

/**
 * Modal pubblica del Brain Lab esposta a tutti gli utenti dalla HomePage.
 *
 * Mostra i pattern già discoverati dall'admin in tab Low-Odds / High-Odds.
 * Per ogni candidato sono disponibili 2 azioni:
 *   - 🔬 Backtest: rilancia il pattern sugli ultimi 30 giorni
 *   - 💾 Aggiungi: prompt nome + salva in localStorage["bc_saved_presets"]
 *     così l'utente lo ritrova nei suoi "Filtri Salvati" della HomePage.
 *
 * Props:
 *   - onClose: () => void
 *   - onPresetAdded: () => void   (callback per refresh dei saved presets)
 */
export default function PublicBrainLabModal({ onClose, onPresetAdded }) {
  const navigate = useNavigate();

  // Reusable Premium-required CTA shown inline when an API call returns 401/402.
  const PremiumCTA = ({ onClose: closeFn }) => (
    <div data-testid="brain-lab-premium-cta" style={{
      padding: "1rem 1.1rem",
      background: "linear-gradient(135deg, rgba(200,255,0,0.06) 0%, rgba(167,139,250,0.05) 100%)",
      border: "1px solid rgba(200,255,0,0.30)",
      display: "flex", flexDirection: "column", gap: 10,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <Crown size={15} style={{ color: "#C8FF00" }} />
        <span style={{
          fontFamily: "'JetBrains Mono',monospace",
          fontSize: "0.65rem",
          letterSpacing: "0.18em",
          textTransform: "uppercase",
          color: "#C8FF00",
        }}>Funzione Premium</span>
      </div>
      <div style={{
        fontFamily: "'Barlow Condensed',sans-serif",
        fontWeight: 800,
        fontSize: "1.1rem",
        letterSpacing: "0.03em",
        textTransform: "uppercase",
        color: C.text,
        lineHeight: 1.15,
      }}>
        Sblocca le combo vincenti del Brain Lab
      </div>
      <div style={{ color: C.muted, fontSize: "0.78rem", lineHeight: 1.55 }}>
        La scansione completa con backtest reale è una feature Premium.
        Effettua l'accesso o attiva un piano per usarla in pieno.
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <button
          data-testid="brain-lab-cta-pricing"
          onClick={() => { closeFn?.(); navigate("/pricing"); }}
          style={{
            padding: "0.55rem 0.9rem",
            background: "#C8FF00", color: "#020202", border: "none",
            fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 800,
            letterSpacing: "0.12em", textTransform: "uppercase",
            fontSize: "0.8rem", cursor: "pointer",
            display: "inline-flex", alignItems: "center", gap: 6,
          }}
        >
          <Sparkles size={12} /> Scopri Premium
        </button>
        <button
          data-testid="brain-lab-cta-login"
          onClick={() => { closeFn?.(); navigate("/auth?redirect=/"); }}
          style={{
            padding: "0.55rem 0.9rem",
            background: "transparent", color: C.text,
            border: `1px solid ${C.border}`,
            fontFamily: "'JetBrains Mono',monospace",
            letterSpacing: "0.10em", textTransform: "uppercase",
            fontSize: "0.7rem", cursor: "pointer",
          }}
        >
          Accedi
        </button>
      </div>
    </div>
  );

  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bucket, setBucket] = useState("high_odds");
  const [backtest, setBacktest] = useState(null); // candidate o null
  const [addingFor, setAddingFor] = useState(null); // candidate o null
  const [addName, setAddName] = useState("");
  const [savedKeys, setSavedKeys] = useState(new Set());
  const [maxThreshold, setMaxThreshold] = useState("");
  const [job, setJob] = useState(null);
  const [discoveryStarting, setDiscoveryStarting] = useState(false);
  const [discoveryError, setDiscoveryError] = useState(null);

  // Custom Scan: l'utente sceglie 1-3 filtri + 1 mercato target
  const [view, setView] = useState("discovery"); // "discovery" | "custom"
  const [customMode, setCustomMode] = useState("filters"); // "filters" | "market-only"
  const [customKeys, setCustomKeys] = useState([]);
  const [customMarket, setCustomMarket] = useState("");
  const [customScanning, setCustomScanning] = useState(false);
  const [customResult, setCustomResult] = useState(null);
  const [customError, setCustomError] = useState(null);
  // Target-market scan (market-only mode)
  const [targetMarket, setTargetMarket] = useState("");
  const [targetScanning, setTargetScanning] = useState(false);
  const [targetResult, setTargetResult] = useState(null);
  const [targetError, setTargetError] = useState(null);

  const loadStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/brain-lab/public-job-status`, { headers: authHeaders() });
      setJob(res.data?.job || null);
    } catch { /* silent */ }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/brain-lab/public-candidates?limit=50`, { headers: authHeaders() });
      setCandidates(res.data?.candidates || []);
    } catch {
      setCandidates([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    loadStatus();
    // Pre-popola savedKeys dai preset attuali per evidenziare i già salvati
    try {
      const presets = JSON.parse(localStorage.getItem("bc_saved_presets") || "[]");
      const set = new Set();
      presets.forEach((p) => {
        if (p?.cross?.length && p?.minp != null) {
          set.add(p.cross.slice().sort().join("|") + ":" + p.minp);
        }
      });
      setSavedKeys(set);
    } catch { /* ignore */ }
  }, [load, loadStatus]);

  // Polling job status mentre è in computing
  useEffect(() => {
    const status = job?.status;
    if (status !== "pending" && status !== "loading" && status !== "computing") return;
    const id = setInterval(loadStatus, 2500);
    return () => clearInterval(id);
  }, [job?.status, loadStatus]);

  // Quando il job arriva a "done", ricarica i candidati e auto-switcha il
  // tab al mode appena calcolato, così l'utente vede subito i risultati
  // (es. se il job era "low_odds" e l'utente era sul tab high_odds vuoto).
  useEffect(() => {
    if (job?.status === "done") {
      load();
      if (job.mode === "low_odds" || job.mode === "high_odds") {
        setBucket(job.mode);
      }
    }
  }, [job?.status, job?.mode, load]);

  const handleDiscover = async (mode) => {
    if (discoveryStarting) return;
    setDiscoveryStarting(true);
    setDiscoveryError(null);
    try {
      const payload = { mode };
      if (maxThreshold) payload.max_threshold = parseInt(maxThreshold, 10);
      const res = await axios.post(`${API}/brain-lab/public-discover`, payload, { headers: authHeaders() });
      // Leggiamo SEMPRE lo stato reale dal server (incl. progress reale) invece
      // di settare progress=0 lato client. Evita il "boom al 100%" quando il
      // job era già done o quasi quando l'utente clicca.
      let activeJob = null;
      try {
        const st = await axios.get(`${API}/brain-lab/public-job-status?job_id=${res.data.job_id}`, { headers: authHeaders() });
        activeJob = st.data?.job || null;
        setJob(activeJob);
      } catch { /* fallback below */ }
      if (res.data?.already_running) {
        const activeMode = (activeJob?.mode || "").toUpperCase();
        const progress = activeJob?.progress || 0;
        if (activeMode && activeMode !== mode.toUpperCase()) {
          setDiscoveryError(
            `Discovery ${activeMode} già in corso · ${progress}%. Attendi che finisca, poi rilancia ${mode.toUpperCase()} per ottenere i pattern di quel bucket.`,
          );
        } else {
          setDiscoveryError(`Discovery già in corso · ${progress}%. Mostro lo stato live.`);
        }
      }
    } catch (err) {
      if (isAuthOrPremiumError(err)) {
        setDiscoveryError({ type: "premium" });
      } else {
        setDiscoveryError(err?.response?.data?.detail || "Discovery fallita");
      }
    } finally {
      setDiscoveryStarting(false);
    }
  };

  const [cancelling, setCancelling] = useState(false);

  const handleCancelDiscovery = async () => {
    if (!job?.job_id) return;
    if (!window.confirm("Interrompere la discovery in corso?")) return;
    setCancelling(true);
    try {
      await axios.post(
        `${API}/brain-lab/public-cancel-job?job_id=${job.job_id}`,
        {},
        { headers: authHeaders() },
      );
      // Force a status refresh so UI exits the in-progress state quickly.
      try {
        const st = await axios.get(`${API}/brain-lab/public-job-status?job_id=${job.job_id}`, { headers: authHeaders() });
        setJob(st.data?.job || null);
      } catch { /* poll loop will catch up */ }
      setDiscoveryError("Discovery interrotta dall'utente.");
    } catch (err) {
      setDiscoveryError(err?.response?.data?.detail || "Impossibile interrompere il job.");
    } finally {
      setCancelling(false);
    }
  };

  const inProgress = ["pending", "loading", "computing"].includes(job?.status);
  const filtered = candidates.filter((c) => c.mode_bucket === bucket);

  const toggleCustomKey = (k) => {
    setCustomKeys((prev) => {
      if (prev.includes(k)) return prev.filter((x) => x !== k);
      if (prev.length >= 3) return prev;
      return [...prev, k];
    });
  };

  const handleCustomScan = async () => {
    if (!customKeys.length || !customMarket) return;
    setCustomScanning(true);
    setCustomError(null);
    setCustomResult(null);
    try {
      const res = await axios.post(
        `${API}/brain-lab/custom-scan`,
        { keys: customKeys, market: customMarket },
        { headers: authHeaders() },
      );
      setCustomResult(res.data);
    } catch (err) {
      setCustomError(err?.response?.data?.detail || "Scansione fallita");
    } finally {
      setCustomScanning(false);
    }
  };

  const handleTargetMarketScan = async () => {
    if (!targetMarket) return;
    setTargetScanning(true);
    setTargetError(null);
    setTargetResult(null);
    try {
      const res = await axios.post(
        `${API}/brain-lab/target-market-scan`,
        { market: targetMarket, top_k: 10, max_combo_size: 3, min_n: 100, min_edge_pp: 3.0 },
        { headers: authHeaders(), timeout: 90000 },
      );
      setTargetResult(res.data);
    } catch (err) {
      setTargetError(extractError(err));
    } finally {
      setTargetScanning(false);
    }
  };

  const handleAddSubmit = () => {
    if (!addingFor) return;
    const trimmed = (addName || "").trim().slice(0, 32);
    if (!trimmed) return;
    let arr = [];
    try {
      arr = JSON.parse(localStorage.getItem("bc_saved_presets") || "[]");
    } catch { /* keep [] */ }
    // remove duplicate name
    arr = arr.filter((x) => x.name !== trimmed);
    arr.push({
      name: trimmed,
      cross: addingFor.keys,
      minp: addingFor.threshold,
      pred: "all",
      vb: false,
      source: "brainlab",
      market_hint: addingFor.market,
      hit_rate: addingFor.hit_rate,
    });
    localStorage.setItem("bc_saved_presets", JSON.stringify(arr));
    const sigKey = addingFor.keys.slice().sort().join("|") + ":" + addingFor.threshold;
    setSavedKeys(new Set([...savedKeys, sigKey]));
    setAddingFor(null);
    setAddName("");
    onPresetAdded?.();
  };

  return (
    <div
      data-testid="public-brainlab-modal"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1050,
        background: C.bg,
        backdropFilter: "blur(8px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "1.5rem",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: C.panel,
          border: `1px solid ${C.cyan}44`,
          boxShadow: `0 0 32px ${C.cyan}22`,
          maxWidth: 1080,
          width: "100%",
          maxHeight: "92vh",
          display: "flex",
          flexDirection: "column",
          position: "relative",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            height: 2,
            width: "100%",
            background: `linear-gradient(90deg, transparent, ${C.cyan}, ${C.violet}, transparent)`,
          }}
        />

        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.6rem",
            padding: "1rem 1.25rem",
            borderBottom: `1px solid ${C.border}`,
          }}
        >
          <FlaskConical size={15} style={{ color: C.cyan, filter: `drop-shadow(0 0 6px ${C.cyan})` }} />
          <span
            style={{
              fontFamily: "'Barlow Condensed',sans-serif",
              fontWeight: 700,
              fontSize: "0.9rem",
              letterSpacing: "0.15em",
              textTransform: "uppercase",
              color: C.text,
            }}
          >
            Brain Lab · Pattern Discovery
          </span>
          <span
            style={{
              padding: "0.15rem 0.5rem",
              background: `${C.violet}14`,
              border: `1px solid ${C.violet}40`,
              color: C.violet,
              fontFamily: "'JetBrains Mono',monospace",
              fontSize: "0.58rem",
              letterSpacing: "0.1em",
            }}
          >
            CRISTALLO ENGINE
          </span>
          <button
            data-testid="public-brainlab-refresh"
            onClick={load}
            title="Ricarica"
            style={{
              marginLeft: "auto",
              background: "none",
              border: "none",
              color: C.muted,
              cursor: "pointer",
              padding: 4,
            }}
          >
            <RefreshCw size={13} className={loading ? "animate-spin" : ""} />
          </button>
          <button
            data-testid="public-brainlab-close"
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              color: C.muted,
              cursor: "pointer",
              padding: 4,
            }}
          >
            <X size={15} />
          </button>
        </div>

        {/* Intro */}
        <div style={{ padding: "0.7rem 1.25rem", fontSize: "0.74rem", color: C.muted, fontFamily: "'DM Sans',sans-serif", lineHeight: 1.5, borderBottom: `1px solid ${C.border}` }}>
          Pattern statistici scoperti su tutto lo storico delle partite. Clicca <span style={{ color: C.cyan }}>Backtest</span> per riverificarli sulle ultime 30 giorni di partite reali, oppure <span style={{ color: C.green }}>Aggiungi</span> per salvarli nei tuoi filtri della HomePage.
        </div>

        {/* View toggle: Discovery vs Custom Scan */}
        <div style={{ display: "flex", gap: 0, padding: "0.5rem 1.25rem 0", borderBottom: `1px solid ${C.border}` }}>
          {[
            { key: "discovery", label: "Discovery automatica", icon: Sparkles },
            { key: "custom", label: "Scansione custom", icon: Crosshair },
          ].map((v) => {
            const active = view === v.key;
            const Icon = v.icon;
            return (
              <button
                key={v.key}
                data-testid={`public-brainlab-view-${v.key}`}
                onClick={() => setView(v.key)}
                style={{
                  padding: "0.6rem 1.1rem",
                  background: "none",
                  border: "none",
                  borderBottom: active ? `2px solid ${C.cyan}` : "2px solid transparent",
                  color: active ? C.cyan : C.muted,
                  fontFamily: "'Barlow Condensed',sans-serif",
                  fontWeight: 700,
                  fontSize: "0.78rem",
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  cursor: "pointer",
                  filter: active ? `drop-shadow(0 0 4px ${C.cyan})` : "none",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <Icon size={12} />
                {v.label}
              </button>
            );
          })}
        </div>

        {view === "discovery" && (
        <>
        {/* Discovery controls */}
        <div style={{ padding: "0.85rem 1.25rem", borderBottom: `1px solid ${C.border}`, background: "rgba(167,139,250,0.03)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", flexWrap: "wrap", marginBottom: "0.5rem" }}>
            <span style={{
              fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700,
              fontSize: "0.62rem", color: C.muted, letterSpacing: "0.14em",
              textTransform: "uppercase",
            }}>
              Soglia ≥% max:
            </span>
            {["", "40", "50", "55", "60", "65", "70"].map((t) => {
              const active = maxThreshold === t;
              return (
                <button
                  key={t || "any"}
                  data-testid={`public-brainlab-maxthr-${t || "any"}`}
                  onClick={() => setMaxThreshold(t)}
                  disabled={inProgress || discoveryStarting}
                  style={{
                    padding: "0.28rem 0.5rem",
                    background: active ? `${C.violet}22` : "transparent",
                    color: active ? C.violet : C.muted,
                    border: `1px solid ${active ? C.violet + "88" : C.border}`,
                    fontFamily: "'JetBrains Mono',monospace",
                    fontSize: "0.62rem",
                    cursor: (inProgress || discoveryStarting) ? "wait" : "pointer",
                    filter: active ? `drop-shadow(0 0 4px ${C.violet})` : "none",
                    opacity: (inProgress || discoveryStarting) ? 0.6 : 1,
                  }}
                >
                  {t === "" ? "ANY" : `≤${t}`}
                </button>
              );
            })}
          </div>
          <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap", alignItems: "center" }}>
            <button
              data-testid="public-brainlab-discover-low"
              disabled={discoveryStarting || inProgress}
              onClick={() => handleDiscover("low_odds")}
              style={discoverBtn(C.cyan, discoveryStarting || inProgress)}
            >
              {(inProgress && job?.mode === "low_odds") ? <Loader2 size={10} className="animate-spin" /> : <Sparkles size={10} />}
              Low-Odds
            </button>
            <button
              data-testid="public-brainlab-discover-high"
              disabled={discoveryStarting || inProgress}
              onClick={() => handleDiscover("high_odds")}
              style={discoverBtn(C.amber, discoveryStarting || inProgress)}
            >
              {(inProgress && job?.mode === "high_odds") ? <Loader2 size={10} className="animate-spin" /> : <TrendingUp size={10} />}
              High-Odds
            </button>
            <button
              data-testid="public-brainlab-discover-both"
              disabled={discoveryStarting || inProgress}
              onClick={() => handleDiscover("both")}
              style={discoverBtn(C.violet, discoveryStarting || inProgress)}
            >
              {(inProgress && job?.mode === "both") ? <Loader2 size={10} className="animate-spin" /> : <Zap size={10} />}
              Both
            </button>
            {inProgress && (
              <button
                data-testid="public-brainlab-cancel"
                onClick={handleCancelDiscovery}
                disabled={cancelling}
                style={{
                  padding: "0.45rem 0.75rem",
                  background: cancelling ? "transparent" : `${C.red}1A`,
                  border: `1px solid ${C.red}77`,
                  color: C.red,
                  fontFamily: "'JetBrains Mono',monospace",
                  fontSize: "0.62rem",
                  letterSpacing: "0.10em",
                  textTransform: "uppercase",
                  cursor: cancelling ? "wait" : "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 5,
                }}
                title="Interrompi la discovery in corso"
              >
                {cancelling ? <Loader2 size={10} className="animate-spin" /> : <XCircle size={10} />}
                {cancelling ? "Interruzione…" : "Interrompi"}
              </button>
            )}
            <span style={{
              fontSize: "0.62rem", color: C.muted, fontFamily: "'DM Sans',sans-serif",
              fontStyle: "italic", marginLeft: "0.3rem",
            }}>
              Soglie più basse = pattern più larghi (più partite)
            </span>
          </div>
          {discoveryError && (
            typeof discoveryError === "object" && discoveryError.type === "premium" ? (
              <div style={{ marginTop: "0.5rem" }}><PremiumCTA closeFn={onClose} /></div>
            ) : (
              <div style={{
                marginTop: "0.5rem",
                padding: "0.35rem 0.6rem",
                background: `${C.amber}14`,
                border: `1px solid ${C.amber}55`,
                color: C.amber,
                fontFamily: "'DM Sans',sans-serif", fontSize: "0.7rem",
              }}>
                {discoveryError}
              </div>
            )
          )}
          {job && (inProgress || job.status === "done" || job.status === "error" || job.status === "cancelled") && (
            <div style={{
              marginTop: "0.55rem",
              padding: "0.5rem 0.7rem",
              background: C.panelElev,
              border: `1px solid ${C.border}`,
              fontFamily: "'JetBrains Mono',monospace", fontSize: "0.66rem", color: C.muted,
            }}>
              <div style={{ display: "flex", gap: "0.9rem", flexWrap: "wrap", marginBottom: 5 }}>
                <span>STATUS <span style={{ color: job.status === "done" ? C.green : job.status === "error" ? C.red : job.status === "cancelled" ? C.amber : C.amber }}>{job.status?.toUpperCase()}</span></span>
                <span>MODE <span style={{ color: C.text }}>{(job.mode || "both").toUpperCase()}</span></span>
                {job.max_threshold && <span>MAX≥% <span style={{ color: C.violet }}>{job.max_threshold}</span></span>}
                <span>PROGRESS <span style={{ color: C.text }}>{job.progress || 0}%</span></span>
                {job.matches_loaded != null && <span>MATCH <span style={{ color: C.text }}>{job.matches_loaded}</span></span>}
                {job.candidates_total != null && <span>CAND <span style={{ color: C.green }}>{job.candidates_total}</span></span>}
              </div>
              <div style={{ height: 3, background: C.border, position: "relative", overflow: "hidden" }}>
                <div style={{
                  position: "absolute", inset: 0,
                  width: `${Math.max(0, Math.min(100, job.progress || 0))}%`,
                  background: `linear-gradient(90deg, ${C.cyan}, ${C.violet})`,
                  boxShadow: `0 0 6px ${C.cyan}`,
                  transition: "width 0.4s ease",
                }} />
              </div>
            </div>
          )}
        </div>

        {/* Tabs bucket */}
        <div style={{ display: "flex", gap: "0.4rem", padding: "0.5rem 1.25rem 0", borderBottom: `1px solid ${C.border}` }}>
          {[
            { key: "low_odds", label: "Low-Odds · Hit alta", color: C.cyan },
            { key: "high_odds", label: "High-Odds · Quote più alte", color: C.amber },
          ].map((t) => {
            const active = bucket === t.key;
            const count = candidates.filter((c) => c.mode_bucket === t.key).length;
            return (
              <button
                key={t.key}
                data-testid={`public-brainlab-tab-${t.key}`}
                onClick={() => setBucket(t.key)}
                style={{
                  padding: "0.55rem 0.9rem",
                  background: "none",
                  border: "none",
                  borderBottom: active ? `2px solid ${t.color}` : "2px solid transparent",
                  color: active ? t.color : C.muted,
                  fontFamily: "'Barlow Condensed',sans-serif",
                  fontWeight: 700,
                  fontSize: "0.78rem",
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                  cursor: "pointer",
                  filter: active ? `drop-shadow(0 0 4px ${t.color})` : "none",
                }}
              >
                {t.label} <span style={{ opacity: 0.6, marginLeft: 4 }}>· {count}</span>
              </button>
            );
          })}
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: "1rem 1.25rem" }}>
          {loading ? (
            <div style={{ padding: "3rem", textAlign: "center", color: C.muted }}>
              <Loader2 size={20} className="animate-spin" style={{ color: C.cyan }} />
              <div style={{ marginTop: 10, fontFamily: "'DM Sans',sans-serif", fontSize: "0.8rem" }}>
                Caricamento pattern...
              </div>
            </div>
          ) : filtered.length === 0 ? (
            <div
              style={{
                padding: "2rem",
                textAlign: "center",
                fontFamily: "'DM Sans',sans-serif",
                fontSize: "0.78rem",
                color: C.muted,
                border: `1px dashed ${C.border}`,
              }}
            >
              Nessun pattern disponibile per questo bucket.
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table data-testid="public-brainlab-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.74rem" }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                    {["Filtri (∩)", "≥%", "Mercato target", "Hit %", "Edge", "N", "Quota", "Azioni"].map((h) => (
                      <th
                        key={h}
                        style={{
                          textAlign: "left",
                          padding: "0.5rem",
                          color: C.muted,
                          fontFamily: "'Barlow Condensed',sans-serif",
                          fontWeight: 700,
                          letterSpacing: "0.12em",
                          textTransform: "uppercase",
                          fontSize: "0.64rem",
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((c, idx) => {
                    const sigKey = c.keys.slice().sort().join("|") + ":" + c.threshold;
                    const alreadySaved = savedKeys.has(sigKey);
                    const edgeColor = c.edge_pp >= 20 ? C.green : c.edge_pp >= 12 ? C.cyan : C.amber;
                    return (
                      <tr key={sigKey + ":" + idx} style={{ borderBottom: `1px solid ${C.border}` }}>
                        <td style={{ padding: "0.5rem" }}>
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                            {c.label_keys.map((lk, i) => (
                              <span
                                key={i}
                                style={{
                                  padding: "0.1rem 0.4rem",
                                  background: `${C.cyan}14`,
                                  border: `1px solid ${C.cyan}40`,
                                  color: C.cyan,
                                  fontFamily: "'JetBrains Mono',monospace",
                                  fontSize: "0.62rem",
                                }}
                              >
                                {lk}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td style={{ padding: "0.5rem", color: C.text, fontFamily: "'JetBrains Mono',monospace" }}>{c.threshold}</td>
                        <td style={{ padding: "0.5rem", color: C.violet, fontWeight: 600 }}>{c.label_market}</td>
                        <td style={{ padding: "0.5rem", color: C.green, fontFamily: "'JetBrains Mono',monospace", fontWeight: 700 }}>
                          {c.hit_rate}
                        </td>
                        <td style={{ padding: "0.5rem", color: edgeColor, fontFamily: "'JetBrains Mono',monospace", fontWeight: 700 }}>
                          +{c.edge_pp}pp
                        </td>
                        <td style={{ padding: "0.5rem", color: C.muted, fontFamily: "'JetBrains Mono',monospace" }}>{c.n}</td>
                        <td style={{ padding: "0.5rem", color: C.text, fontFamily: "'JetBrains Mono',monospace" }}>
                          {c.quote_est ? `~${c.quote_est}` : "—"}
                        </td>
                        <td style={{ padding: "0.5rem", display: "flex", gap: 5 }}>
                          <button
                            data-testid={`public-brainlab-backtest-${idx}`}
                            onClick={() => setBacktest(c)}
                            style={actionBtn(C.cyan)}
                            title="Backtest ultimi 30gg"
                          >
                            <Beaker size={9} /> Backtest
                          </button>
                          <button
                            data-testid={`public-brainlab-add-${idx}`}
                            onClick={() => {
                              setAddingFor(c);
                              setAddName(
                                (c.label_keys || []).slice(0, 3).join(" + ").slice(0, 26)
                              );
                            }}
                            disabled={alreadySaved}
                            style={actionBtn(alreadySaved ? C.muted : C.green, alreadySaved)}
                            title={alreadySaved ? "Già nei tuoi filtri" : "Aggiungi ai filtri"}
                          >
                            <Plus size={9} /> {alreadySaved ? "Salvato" : "Aggiungi"}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
        </>
        )}

        {view === "custom" && (
          <div style={{ flex: 1, overflowY: "auto", padding: "1rem 1.25rem" }}>
            {/* Mode switch: filters-first vs market-first */}
            <div style={{
              display: "flex", gap: 4, marginBottom: "1rem",
              padding: 4, background: C.surface, border: `1px solid ${C.border}`,
            }}>
              <button
                data-testid="custom-mode-filters"
                onClick={() => setCustomMode("filters")}
                style={{
                  flex: 1, padding: "0.5rem 0.8rem",
                  background: customMode === "filters" ? `${C.cyan}22` : "transparent",
                  color: customMode === "filters" ? C.cyan : C.muted,
                  border: "none",
                  fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700,
                  fontSize: "0.74rem", letterSpacing: "0.1em", textTransform: "uppercase",
                  cursor: "pointer",
                }}
              >
                🔍 Filtri → Mercato
              </button>
              <button
                data-testid="custom-mode-market"
                onClick={() => setCustomMode("market-only")}
                style={{
                  flex: 1, padding: "0.5rem 0.8rem",
                  background: customMode === "market-only" ? `${C.violet}22` : "transparent",
                  color: customMode === "market-only" ? C.violet : C.muted,
                  border: "none",
                  fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700,
                  fontSize: "0.74rem", letterSpacing: "0.1em", textTransform: "uppercase",
                  cursor: "pointer",
                }}
              >
                🎯 Mercato → Filtri
              </button>
            </div>

            {customMode === "filters" && (
            <div style={{ fontSize: "0.74rem", color: C.muted, fontFamily: "'DM Sans',sans-serif", lineHeight: 1.5, marginBottom: "1rem" }}>
              <strong style={{ color: C.cyan }}>Modalità Filtri → Mercato:</strong> tu scegli 1–3 filtri da incrociare + 1 mercato target. Il sistema calcola la <span style={{ color: C.cyan }}>percentuale di esiti verificati</span> nel passato per ogni soglia minima del cross-score, identifica la <span style={{ color: C.green }}>soglia ottimale</span> e ti suggerisce, se esiste, un <span style={{ color: C.amber }}>mercato alternativo</span> che ha performato meglio con gli stessi filtri.
            </div>
            )}

            {customMode === "market-only" && (
            <div style={{ fontSize: "0.74rem", color: C.muted, fontFamily: "'DM Sans',sans-serif", lineHeight: 1.5, marginBottom: "1rem" }}>
              <strong style={{ color: C.violet }}>Modalità Mercato → Filtri:</strong> tu scegli SOLO il mercato target (es. <em>X</em>, <em>Gol</em>, <em>Over 2.5</em>). Il sistema esplora <span style={{ color: C.violet }}>oltre 1.500 combinazioni</span> di 1–3 filtri sul tuo storico, e ti restituisce le <span style={{ color: C.green }}>top 10 combinazioni</span> che hanno avuto il miglior hit_rate per quel mercato — con la <span style={{ color: C.amber }}>soglia ottimale</span> già calcolata. Perfetto quando sai <em>cosa</em> vuoi giocare ma non <em>quali pattern</em> guardare.
            </div>
            )}

            {customMode === "filters" && (
            <>
            {/* Step 1: filtri */}
            <div style={{ marginBottom: "1rem" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: "0.5rem" }}>
                <span style={{
                  padding: "0.15rem 0.45rem", background: `${C.cyan}22`, color: C.cyan,
                  fontFamily: "'JetBrains Mono',monospace", fontSize: "0.65rem", fontWeight: 700,
                }}>1</span>
                <span style={{
                  fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700,
                  fontSize: "0.78rem", letterSpacing: "0.12em", textTransform: "uppercase", color: C.text,
                }}>
                  Scegli i filtri ({customKeys.length}/3)
                </span>
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                {CHIP_OPTIONS.map((c) => {
                  const active = customKeys.includes(c.key);
                  const disabled = !active && customKeys.length >= 3;
                  return (
                    <button
                      key={c.key}
                      data-testid={`custom-key-${c.key}`}
                      onClick={() => toggleCustomKey(c.key)}
                      disabled={disabled}
                      style={{
                        padding: "0.32rem 0.55rem",
                        background: active ? `${C.cyan}28` : "transparent",
                        color: active ? C.cyan : (disabled ? C.muted + "88" : C.muted),
                        border: `1px solid ${active ? C.cyan + "88" : C.border}`,
                        fontFamily: "'JetBrains Mono',monospace", fontSize: "0.66rem",
                        cursor: disabled ? "not-allowed" : "pointer",
                        filter: active ? `drop-shadow(0 0 4px ${C.cyan})` : "none",
                        opacity: disabled ? 0.45 : 1,
                      }}
                    >
                      {c.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Step 2: mercato target */}
            <div style={{ marginBottom: "1rem" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: "0.5rem" }}>
                <span style={{
                  padding: "0.15rem 0.45rem", background: `${C.violet}22`, color: C.violet,
                  fontFamily: "'JetBrains Mono',monospace", fontSize: "0.65rem", fontWeight: 700,
                }}>2</span>
                <span style={{
                  fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700,
                  fontSize: "0.78rem", letterSpacing: "0.12em", textTransform: "uppercase", color: C.text,
                }}>
                  Mercato target {customMarket && <span style={{ color: C.violet, marginLeft: 6 }}>· {MARKET_OPTIONS.find(m => m.key === customMarket)?.label}</span>}
                </span>
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                {MARKET_OPTIONS.map((m) => {
                  const active = customMarket === m.key;
                  return (
                    <button
                      key={m.key}
                      data-testid={`custom-market-${m.key}`}
                      onClick={() => setCustomMarket(m.key)}
                      style={{
                        padding: "0.32rem 0.55rem",
                        background: active ? `${C.violet}28` : "transparent",
                        color: active ? C.violet : C.muted,
                        border: `1px solid ${active ? C.violet + "88" : C.border}`,
                        fontFamily: "'JetBrains Mono',monospace", fontSize: "0.66rem",
                        cursor: "pointer",
                        filter: active ? `drop-shadow(0 0 4px ${C.violet})` : "none",
                      }}
                    >
                      {m.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Step 3: scan button */}
            <div style={{ display: "flex", gap: "0.6rem", alignItems: "center", marginBottom: "1.2rem" }}>
              <button
                data-testid="custom-scan-btn"
                onClick={handleCustomScan}
                disabled={!customKeys.length || !customMarket || customScanning}
                style={{
                  padding: "0.55rem 1.1rem",
                  background: (customKeys.length && customMarket && !customScanning) ? `${C.green}1A` : "transparent",
                  color: (customKeys.length && customMarket && !customScanning) ? C.green : C.muted,
                  border: `1px solid ${(customKeys.length && customMarket && !customScanning) ? C.green + "77" : C.border}`,
                  fontFamily: "'Barlow Condensed',sans-serif",
                  fontWeight: 800, fontSize: "0.78rem",
                  letterSpacing: "0.14em", textTransform: "uppercase",
                  cursor: (customKeys.length && customMarket && !customScanning) ? "pointer" : "not-allowed",
                  display: "inline-flex", alignItems: "center", gap: 7,
                  boxShadow: (customKeys.length && customMarket && !customScanning) ? `0 0 12px ${C.green}33` : "none",
                }}
              >
                {customScanning ? <Loader2 size={12} className="animate-spin" /> : <Target size={12} />}
                {customScanning ? "Scansione in corso…" : "Avvia scansione"}
              </button>
              <button
                onClick={() => { setCustomKeys([]); setCustomMarket(""); setCustomResult(null); setCustomError(null); }}
                disabled={customScanning}
                style={{
                  padding: "0.55rem 0.85rem",
                  background: "transparent",
                  color: C.muted,
                  border: `1px solid ${C.border}`,
                  fontFamily: "'Barlow Condensed',sans-serif",
                  fontWeight: 700, fontSize: "0.72rem",
                  letterSpacing: "0.12em", textTransform: "uppercase",
                  cursor: "pointer",
                }}
              >
                Reset
              </button>
            </div>

            {customError && (
              customError.type === "premium" ? (
                <PremiumCTA closeFn={onClose} />
              ) : (
                <div style={{
                  padding: "0.6rem 0.85rem",
                  background: `${C.red}14`,
                  border: `1px solid ${C.red}55`,
                  color: C.red,
                  fontFamily: "'DM Sans',sans-serif", fontSize: "0.78rem",
                }}>
                  ⚠ {customError.message}
                </div>
              )
            )}

            {customResult && <CustomScanResult result={customResult} C={C} />}
            </>
            )}

            {customMode === "market-only" && (
            <>
            <div style={{ marginBottom: "1rem" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: "0.5rem" }}>
                <span style={{
                  padding: "0.15rem 0.45rem", background: `${C.violet}22`, color: C.violet,
                  fontFamily: "'JetBrains Mono',monospace", fontSize: "0.65rem", fontWeight: 700,
                }}>1</span>
                <span style={{
                  fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700,
                  fontSize: "0.78rem", letterSpacing: "0.12em", textTransform: "uppercase", color: C.text,
                }}>
                  Scegli il mercato target {targetMarket && <span style={{ color: C.violet, marginLeft: 6 }}>· {MARKET_OPTIONS.find(m => m.key === targetMarket)?.label}</span>}
                </span>
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                {MARKET_OPTIONS.map((m) => {
                  const active = targetMarket === m.key;
                  return (
                    <button
                      key={m.key}
                      data-testid={`target-market-${m.key}`}
                      onClick={() => setTargetMarket(m.key)}
                      style={{
                        padding: "0.32rem 0.55rem",
                        background: active ? `${C.violet}28` : "transparent",
                        color: active ? C.violet : C.muted,
                        border: `1px solid ${active ? C.violet + "88" : C.border}`,
                        fontFamily: "'JetBrains Mono',monospace", fontSize: "0.66rem",
                        cursor: "pointer",
                        filter: active ? `drop-shadow(0 0 4px ${C.violet})` : "none",
                      }}
                    >
                      {m.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div style={{ display: "flex", gap: "0.6rem", alignItems: "center", marginBottom: "1.2rem" }}>
              <button
                data-testid="target-market-scan-btn"
                onClick={handleTargetMarketScan}
                disabled={!targetMarket || targetScanning}
                style={{
                  padding: "0.55rem 1.1rem",
                  background: (targetMarket && !targetScanning) ? `${C.violet}1A` : "transparent",
                  color: (targetMarket && !targetScanning) ? C.violet : C.muted,
                  border: `1px solid ${(targetMarket && !targetScanning) ? C.violet + "77" : C.border}`,
                  fontFamily: "'Barlow Condensed',sans-serif",
                  fontWeight: 800, fontSize: "0.78rem",
                  letterSpacing: "0.14em", textTransform: "uppercase",
                  cursor: (targetMarket && !targetScanning) ? "pointer" : "not-allowed",
                  display: "inline-flex", alignItems: "center", gap: 7,
                  boxShadow: (targetMarket && !targetScanning) ? `0 0 12px ${C.violet}33` : "none",
                }}
              >
                {targetScanning ? <Loader2 size={12} className="animate-spin" /> : <Target size={12} />}
                {targetScanning ? "Esplorazione in corso (10-30s)…" : "Trova le combo migliori"}
              </button>
              <button
                onClick={() => { setTargetMarket(""); setTargetResult(null); setTargetError(null); }}
                disabled={targetScanning}
                style={{
                  padding: "0.55rem 0.85rem",
                  background: "transparent", color: C.muted,
                  border: `1px solid ${C.border}`,
                  fontFamily: "'Barlow Condensed',sans-serif",
                  fontWeight: 700, fontSize: "0.72rem",
                  letterSpacing: "0.12em", textTransform: "uppercase",
                  cursor: "pointer",
                }}
              >
                Reset
              </button>
            </div>

            {targetError && (
              targetError.type === "premium" ? (
                <PremiumCTA closeFn={onClose} />
              ) : (
                <div style={{
                  padding: "0.6rem 0.85rem",
                  background: `${C.red}14`,
                  border: `1px solid ${C.red}55`,
                  color: C.red,
                  fontFamily: "'DM Sans',sans-serif", fontSize: "0.78rem",
                }}>
                  ⚠ {targetError.message}
                </div>
              )
            )}

            {targetResult && (
              <TargetMarketScanResult
                result={targetResult}
                C={C}
                onAdd={(c) => {
                  setAddingFor({
                    keys: c.keys,
                    label_keys: c.label_keys,
                    threshold: c.threshold,
                    market: targetResult.market,
                    label_market: targetResult.label_market,
                    hit_rate: c.hit_rate,
                  });
                  setAddName((c.label_keys || []).slice(0, 3).join(" + ").slice(0, 26));
                }}
                onBacktest={(c) => setBacktest({
                  keys: c.keys,
                  label_keys: c.label_keys,
                  threshold: c.threshold,
                  market: targetResult.market,
                  label_market: targetResult.label_market,
                  hit_rate: c.hit_rate,
                  edge_pp: c.edge_pp,
                  n: c.n,
                  quote_est: c.quote_est,
                })}
              />
            )}
            </>
            )}
          </div>
        )}

      </div>

      {/* Backtest modal */}
      {backtest && (
        <BrainLabBacktestModal
          candidate={backtest}
          days={30}
          onClose={() => setBacktest(null)}
        />
      )}

      {/* Add filter dialog */}
      {addingFor && (
        <div
          onClick={() => setAddingFor(null)}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1200,
            background: "rgba(0,0,0,0.72)",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "1rem",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: C.panel,
              border: `1px solid ${C.green}55`,
              boxShadow: `0 0 24px ${C.green}33`,
              padding: "1.2rem 1.4rem",
              maxWidth: 460,
              width: "100%",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: "0.9rem" }}>
              <Plus size={13} style={{ color: C.green }} />
              <span
                style={{
                  fontFamily: "'Barlow Condensed',sans-serif",
                  fontWeight: 700,
                  fontSize: "0.85rem",
                  letterSpacing: "0.15em",
                  textTransform: "uppercase",
                  color: C.text,
                }}
              >
                Aggiungi ai filtri
              </span>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: "0.85rem" }}>
              {(addingFor.label_keys || []).map((lk, i) => (
                <span
                  key={i}
                  style={{
                    padding: "0.12rem 0.4rem",
                    background: `${C.cyan}14`,
                    border: `1px solid ${C.cyan}40`,
                    color: C.cyan,
                    fontFamily: "'JetBrains Mono',monospace",
                    fontSize: "0.62rem",
                  }}
                >
                  {lk}
                </span>
              ))}
              <span style={{ color: C.muted, fontSize: "0.72rem", marginLeft: 4 }}>≥{addingFor.threshold}%</span>
            </div>
            <label
              style={{
                display: "block",
                fontFamily: "'Barlow Condensed',sans-serif",
                fontWeight: 700,
                fontSize: "0.65rem",
                letterSpacing: "0.15em",
                textTransform: "uppercase",
                color: C.muted,
                marginBottom: 4,
              }}
            >
              Nome del filtro (max 32 char)
            </label>
            <input
              data-testid="public-brainlab-add-name"
              autoFocus
              value={addName}
              onChange={(e) => setAddName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleAddSubmit()}
              maxLength={32}
              placeholder="Es. X2 + MgCasa + Over1.5"
              style={{
                width: "100%",
                background: C.panelElev,
                border: `1px solid ${C.border}`,
                color: C.text,
                padding: "0.6rem 0.7rem",
                fontFamily: "'DM Sans',sans-serif",
                fontSize: "0.82rem",
                outline: "none",
                marginBottom: "1rem",
              }}
            />
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
              <button
                data-testid="public-brainlab-add-cancel"
                onClick={() => setAddingFor(null)}
                style={{
                  padding: "0.5rem 1rem",
                  background: "transparent",
                  color: C.muted,
                  border: `1px solid ${C.border}`,
                  fontFamily: "'Barlow Condensed',sans-serif",
                  fontWeight: 700,
                  fontSize: "0.72rem",
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                  cursor: "pointer",
                }}
              >
                Annulla
              </button>
              <button
                data-testid="public-brainlab-add-confirm"
                onClick={handleAddSubmit}
                disabled={!addName.trim()}
                style={{
                  padding: "0.5rem 1rem",
                  background: C.green,
                  color: "#0A0A0A",
                  border: `1px solid ${C.green}`,
                  fontFamily: "'Barlow Condensed',sans-serif",
                  fontWeight: 800,
                  fontSize: "0.72rem",
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                  cursor: addName.trim() ? "pointer" : "not-allowed",
                  opacity: addName.trim() ? 1 : 0.5,
                  boxShadow: `0 0 14px ${C.green}55`,
                }}
              >
                Salva filtro
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Subset esposto nella UI (i filtri/mercati più comuni per la Scansione Custom)
const CHIP_OPTIONS = [
  { key: "1", label: "1" }, { key: "x", label: "X" }, { key: "2", label: "2" },
  { key: "1x", label: "1X" }, { key: "x2", label: "X2" }, { key: "12", label: "12" },
  { key: "over15", label: "Over 1.5" }, { key: "over25", label: "Over 2.5" },
  { key: "under15", label: "Under 1.5" }, { key: "under25", label: "Under 2.5" }, { key: "under35", label: "Under 3.5" },
  { key: "gol", label: "Gol" }, { key: "nogol", label: "NoGol" },
  { key: "mg13", label: "Multigol 1-3" }, { key: "mg24", label: "Multigol 2-4" },
  { key: "mg_casa", label: "Multigol Casa" }, { key: "mg_trasferta", label: "Multigol Trasferta" },
  { key: "ht_over05", label: "Over 0.5 HT" }, { key: "ht_over15", label: "Over 1.5 HT" },
  { key: "st_over05", label: "Over 0.5 ST" }, { key: "st_over15", label: "Over 1.5 ST" },
  { key: "gundam", label: "Gundam Score" },
];

const MARKET_OPTIONS = [
  { key: "1", label: "1 Casa" }, { key: "X", label: "X Pareggio" }, { key: "2", label: "2 Ospite" },
  { key: "1X", label: "1X" }, { key: "X2", label: "X2" }, { key: "12", label: "12" },
  { key: "over15", label: "Over 1.5" }, { key: "over25", label: "Over 2.5" }, { key: "over35", label: "Over 3.5" },
  { key: "under15", label: "Under 1.5" }, { key: "under25", label: "Under 2.5" }, { key: "under35", label: "Under 3.5" },
  { key: "btts", label: "Gol (BTTS)" }, { key: "no_btts", label: "NoGol" },
  { key: "home_0_1", label: "Casa 0-1" }, { key: "home_0_2", label: "Casa 0-2" },
  { key: "away_0_1", label: "Ospite 0-1" }, { key: "away_0_2", label: "Ospite 0-2" },
  { key: "mg_1_3", label: "Multigol 1-3" }, { key: "mg_2_4", label: "Multigol 2-4" }, { key: "mg_3_plus", label: "Multigol 3+" },
  { key: "1+over15", label: "1 + Over 1.5" }, { key: "2+over15", label: "2 + Over 1.5" },
  { key: "1x+over15", label: "1X + Over 1.5" }, { key: "x2+over15", label: "X2 + Over 1.5" },
  { key: "1+btts", label: "1 + Gol" }, { key: "1x+btts", label: "1X + Gol" }, { key: "x2+btts", label: "X2 + Gol" },
  { key: "ht_over05", label: "Over 0.5 HT" }, { key: "ht_over15", label: "Over 1.5 HT" },
  { key: "st_over05", label: "Over 0.5 ST" }, { key: "st_over15", label: "Over 1.5 ST" },
];

function actionBtn(color, disabled = false) {
  return {
    padding: "0.25rem 0.55rem",
    background: `${color}1A`,
    color,
    border: `1px solid ${color}66`,
    fontFamily: "'Barlow Condensed',sans-serif",
    fontWeight: 700,
    fontSize: "0.62rem",
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    cursor: disabled ? "default" : "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    opacity: disabled ? 0.6 : 1,
  };
}

function discoverBtn(color, disabled) {
  return {
    padding: "0.42rem 0.85rem",
    background: `${color}1A`,
    color,
    border: `1px solid ${color}66`,
    fontFamily: "'Barlow Condensed',sans-serif",
    fontWeight: 700,
    fontSize: "0.68rem",
    letterSpacing: "0.13em",
    textTransform: "uppercase",
    cursor: disabled ? "wait" : "pointer",
    opacity: disabled ? 0.55 : 1,
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    boxShadow: `0 0 8px ${color}33`,
  };
}

function CustomScanResult({ result, C }) {
  const best = result.best_threshold;
  const alt = result.best_alternative;

  return (
    <div data-testid="custom-scan-result" style={{ display: "flex", flexDirection: "column", gap: "1.1rem" }}>
      {/* Header recap */}
      <div style={{
        padding: "0.8rem 0.95rem",
        background: C.panelElev,
        border: `1px solid ${C.border}`,
      }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 5, alignItems: "center", marginBottom: 4 }}>
          {result.label_keys.map((lk, i) => (
            <span key={i} style={{
              padding: "0.12rem 0.45rem",
              background: `${C.cyan}14`, border: `1px solid ${C.cyan}40`, color: C.cyan,
              fontFamily: "'JetBrains Mono',monospace", fontSize: "0.66rem",
            }}>{lk}</span>
          ))}
          <ChevronRight size={11} style={{ color: C.muted, margin: "0 2px" }} />
          <span style={{
            padding: "0.12rem 0.5rem",
            background: `${C.violet}14`, border: `1px solid ${C.violet}40`, color: C.violet,
            fontFamily: "'JetBrains Mono',monospace", fontSize: "0.7rem", fontWeight: 700,
          }}>{result.label_market}</span>
        </div>
        <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: "0.65rem", color: C.muted, display: "flex", gap: "0.8rem", flexWrap: "wrap", marginTop: 6 }}>
          <span>DATASET <span style={{ color: C.text }}>{result.rows_total} match</span></span>
          <span>BASELINE <span style={{ color: C.text }}>{result.baseline}%</span></span>
        </div>
      </div>

      {/* Per-threshold table */}
      <div>
        <div style={{
          fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700,
          fontSize: "0.72rem", letterSpacing: "0.14em", textTransform: "uppercase",
          color: C.muted, marginBottom: 6,
        }}>
          Esiti per soglia minima del cross-score
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.74rem" }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                {["Soglia ≥%", "Hit %", "Baseline", "Edge", "Campione (n)", "Quota fair"].map((h) => (
                  <th key={h} style={{
                    textAlign: "left", padding: "0.45rem 0.55rem",
                    color: C.muted, fontFamily: "'Barlow Condensed',sans-serif",
                    fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase",
                    fontSize: "0.62rem",
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {result.per_threshold.map((p) => {
                const isBest = best && best.threshold === p.threshold;
                const hrColor = p.hit_rate == null ? C.muted : p.hit_rate >= 80 ? C.green : p.hit_rate >= 65 ? C.cyan : p.hit_rate >= 50 ? C.amber : C.red;
                return (
                  <tr key={p.threshold} style={{
                    borderBottom: `1px solid ${C.border}`,
                    background: isBest ? `${C.green}0A` : "transparent",
                  }}>
                    <td style={{ padding: "0.5rem 0.55rem", color: C.text, fontFamily: "'JetBrains Mono',monospace", fontWeight: 700 }}>
                      {p.threshold}
                      {isBest && <span style={{ marginLeft: 6, color: C.green, fontSize: "0.6rem" }}>● OTTIMALE</span>}
                    </td>
                    <td style={{ padding: "0.5rem 0.55rem", color: hrColor, fontFamily: "'JetBrains Mono',monospace", fontWeight: 700 }}>
                      {p.hit_rate != null ? `${p.hit_rate}%` : "—"}
                    </td>
                    <td style={{ padding: "0.5rem 0.55rem", color: C.muted, fontFamily: "'JetBrains Mono',monospace" }}>
                      {p.baseline != null ? `${p.baseline}%` : "—"}
                    </td>
                    <td style={{ padding: "0.5rem 0.55rem", color: p.edge_pp == null ? C.muted : p.edge_pp >= 10 ? C.green : p.edge_pp >= 0 ? C.cyan : C.red, fontFamily: "'JetBrains Mono',monospace", fontWeight: 700 }}>
                      {p.edge_pp != null ? `${p.edge_pp >= 0 ? "+" : ""}${p.edge_pp}pp` : "—"}
                    </td>
                    <td style={{ padding: "0.5rem 0.55rem", color: C.text, fontFamily: "'JetBrains Mono',monospace" }}>
                      {p.n}
                    </td>
                    <td style={{ padding: "0.5rem 0.55rem", color: C.text, fontFamily: "'JetBrains Mono',monospace" }}>
                      {p.quote_est ? `~${p.quote_est}` : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Best threshold verdict */}
      <div style={{
        padding: "0.85rem 1rem",
        background: best ? `${C.green}10` : `${C.amber}10`,
        border: `1px solid ${best ? C.green + "55" : C.amber + "55"}`,
        fontFamily: "'DM Sans',sans-serif", fontSize: "0.82rem",
        color: best ? C.green : C.amber, lineHeight: 1.5,
      }}>
        {best
          ? <>✓ Soglia consigliata: <strong>≥{best.threshold}%</strong> · hit <strong>{best.hit_rate}%</strong> su <strong>{best.n}</strong> match (edge +{best.edge_pp}pp). Quota fair stimata ~{best.quote_est}.</>
          : <>⚠ Nessuna soglia ha prodotto un campione statisticamente robusto (n ≥ 50). Prova con meno filtri o mercato più diffuso.</>
        }
      </div>

      {/* Best alternative suggestion */}
      {alt && (
        <div style={{
          padding: "0.85rem 1rem",
          background: `${C.amber}10`,
          border: `1px solid ${C.amber}55`,
          fontFamily: "'DM Sans',sans-serif", fontSize: "0.82rem",
          color: C.text, lineHeight: 1.5,
        }}>
          <div style={{
            fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700,
            fontSize: "0.72rem", letterSpacing: "0.15em", textTransform: "uppercase",
            color: C.amber, marginBottom: 6,
          }}>
            💡 Suggerimento mercato alternativo
          </div>
          Con gli stessi 3 filtri attivi (soglia ≥{best?.threshold || 50}%), il mercato <strong style={{ color: C.violet }}>{alt.label_market}</strong> ha performato <strong style={{ color: C.green }}>meglio</strong>: hit <strong>{alt.hit_rate}%</strong> (vs {alt.baseline}% baseline, edge +{alt.edge_pp}pp) su <strong>{alt.n}</strong> match{alt.quote_est ? `, quota fair ~${alt.quote_est}` : ""}.
        </div>
      )}

      {/* Top 5 alternatives compact */}
      {result.top_alternatives && result.top_alternatives.length > 0 && (
        <div>
          <div style={{
            fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700,
            fontSize: "0.7rem", letterSpacing: "0.14em", textTransform: "uppercase",
            color: C.muted, marginBottom: 5,
          }}>
            Altri mercati con questi filtri (top 5)
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {result.top_alternatives.map((a, i) => (
              <div key={i} style={{
                display: "grid",
                gridTemplateColumns: "1fr 70px 70px 80px",
                gap: "0.5rem", alignItems: "center",
                padding: "0.35rem 0.6rem",
                background: C.panelElev, border: `1px solid ${C.border}`,
                fontSize: "0.72rem",
              }}>
                <span style={{ color: C.text, fontFamily: "'DM Sans',sans-serif" }}>{a.label_market}</span>
                <span style={{ color: a.hit_rate >= 80 ? C.green : C.cyan, fontFamily: "'JetBrains Mono',monospace", fontWeight: 700 }}>{a.hit_rate}%</span>
                <span style={{ color: a.edge_pp >= 10 ? C.green : a.edge_pp >= 0 ? C.cyan : C.red, fontFamily: "'JetBrains Mono',monospace" }}>+{a.edge_pp}pp</span>
                <span style={{ color: C.muted, fontFamily: "'JetBrains Mono',monospace" }}>n={a.n} {a.quote_est ? `· ~${a.quote_est}` : ""}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


function TargetMarketScanResult({ result, C, onAdd, onBacktest }) {
  const rows = result?.results || [];
  let savedKeys = [];
  try {
    savedKeys = (JSON.parse(localStorage.getItem("bc_saved_presets") || "[]")
      .filter((p) => p.source === "brainlab")
      .map((p) => `${(p.cross || []).slice().sort().join("|")}|${p.minp}|${p.market_hint || ""}`));
  } catch { /* keep [] */ }

  return (
    <div data-testid="target-market-scan-result" style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
      <div style={{
        padding: "0.85rem 1rem",
        background: C.surface,
        border: `1px solid ${C.violet}55`,
        borderLeft: `3px solid ${C.violet}`,
      }}>
        <div style={{
          fontFamily: "'JetBrains Mono',monospace", fontSize: "0.62rem",
          letterSpacing: "0.12em", color: C.violet, textTransform: "uppercase",
          marginBottom: "0.4rem", fontWeight: 700,
        }}>
          Esplorazione completata
        </div>
        <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.82rem", color: C.text, lineHeight: 1.5 }}>
          Mercato analizzato: <strong style={{ color: C.violet }}>{result.label_market}</strong>
          <br />
          Baseline storico: <strong>{result.baseline}%</strong> · Dataset: <strong>{result.rows_total}</strong> partite
          <br />
          Combinazioni esplorate: <strong>{result.combos_evaluated}</strong> · Combo con edge ≥ {result.min_edge_pp}pp: <strong>{result.total_results}</strong>
        </div>
      </div>

      {rows.length === 0 ? (
        <div style={{
          padding: "1rem",
          background: `${C.amber}10`,
          border: `1px dashed ${C.amber}55`,
          color: C.amber,
          fontFamily: "'DM Sans',sans-serif", fontSize: "0.82rem",
          textAlign: "center",
        }}>
          Nessuna combinazione ha superato la soglia minima ({result.min_n} partite + {result.min_edge_pp}pp di edge) per questo mercato. Prova un mercato meno restrittivo.
        </div>
      ) : (
        <>
        <div style={{
          fontFamily: "'JetBrains Mono',monospace", fontSize: "0.65rem",
          color: C.muted, letterSpacing: "0.08em", textTransform: "uppercase",
        }}>
          Top {rows.length} combinazioni · ordinate per Hit Rate
        </div>

        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.74rem" }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                <th style={{ textAlign: "left", padding: "0.5rem", color: C.muted, fontFamily: "'JetBrains Mono',monospace", fontSize: "0.6rem", letterSpacing: "0.1em", textTransform: "uppercase" }}>Combo</th>
                <th style={{ textAlign: "center", padding: "0.5rem", color: C.muted, fontFamily: "'JetBrains Mono',monospace", fontSize: "0.6rem", letterSpacing: "0.1em", textTransform: "uppercase" }}>Soglia</th>
                <th style={{ textAlign: "center", padding: "0.5rem", color: C.muted, fontFamily: "'JetBrains Mono',monospace", fontSize: "0.6rem", letterSpacing: "0.1em", textTransform: "uppercase" }}>Hit Rate</th>
                <th style={{ textAlign: "center", padding: "0.5rem", color: C.muted, fontFamily: "'JetBrains Mono',monospace", fontSize: "0.6rem", letterSpacing: "0.1em", textTransform: "uppercase" }}>Edge</th>
                <th style={{ textAlign: "center", padding: "0.5rem", color: C.muted, fontFamily: "'JetBrains Mono',monospace", fontSize: "0.6rem", letterSpacing: "0.1em", textTransform: "uppercase" }}>n</th>
                <th style={{ textAlign: "center", padding: "0.5rem", color: C.muted, fontFamily: "'JetBrains Mono',monospace", fontSize: "0.6rem", letterSpacing: "0.1em", textTransform: "uppercase" }}>Quota</th>
                <th style={{ padding: "0.5rem" }} />
              </tr>
            </thead>
            <tbody>
              {rows.map((c, idx) => {
                const edgeColor = c.edge_pp >= 10 ? C.green : c.edge_pp >= 5 ? C.amber : C.cyan;
                const sig = `${[...c.keys].sort().join("|")}|${c.threshold}|${result.market}`;
                const alreadySaved = savedKeys.includes(sig);
                return (
                  <tr key={idx} data-testid={`target-result-row-${idx}`} style={{ borderBottom: `1px solid ${C.border}` }}>
                    <td style={{ padding: "0.5rem" }}>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                        {(c.label_keys || []).map((lk, i) => (
                          <span key={i} style={{
                            padding: "0.12rem 0.42rem",
                            background: `${C.cyan}15`,
                            color: C.cyan,
                            border: `1px solid ${C.cyan}55`,
                            fontFamily: "'JetBrains Mono',monospace",
                            fontSize: "0.62rem",
                          }}>{lk}</span>
                        ))}
                      </div>
                    </td>
                    <td style={{ padding: "0.5rem", textAlign: "center", color: C.text, fontFamily: "'JetBrains Mono',monospace", fontWeight: 700 }}>
                      ≥ {c.threshold}
                    </td>
                    <td style={{ padding: "0.5rem", textAlign: "center", color: C.green, fontFamily: "'JetBrains Mono',monospace", fontSize: "0.85rem", fontWeight: 700 }}>
                      {c.hit_rate}%
                    </td>
                    <td style={{ padding: "0.5rem", textAlign: "center", color: edgeColor, fontFamily: "'JetBrains Mono',monospace", fontWeight: 700 }}>
                      +{c.edge_pp}pp
                    </td>
                    <td style={{ padding: "0.5rem", textAlign: "center", color: C.muted, fontFamily: "'JetBrains Mono',monospace" }}>{c.n}</td>
                    <td style={{ padding: "0.5rem", textAlign: "center", color: C.text, fontFamily: "'JetBrains Mono',monospace" }}>
                      {c.quote_est ? `~${c.quote_est}` : "—"}
                    </td>
                    <td style={{ padding: "0.5rem", display: "flex", gap: 5, justifyContent: "flex-end" }}>
                      <button
                        data-testid={`target-result-backtest-${idx}`}
                        onClick={() => onBacktest(c)}
                        style={actionBtn(C.cyan)}
                        title="Backtest ultimi 30gg"
                      >
                        <Beaker size={9} /> Backtest
                      </button>
                      <button
                        data-testid={`target-result-add-${idx}`}
                        onClick={() => onAdd(c)}
                        disabled={alreadySaved}
                        style={actionBtn(alreadySaved ? C.muted : C.green, alreadySaved)}
                        title={alreadySaved ? "Già nei tuoi filtri" : "Aggiungi ai filtri"}
                      >
                        <Plus size={9} /> {alreadySaved ? "Salvato" : "Aggiungi"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        </>
      )}
    </div>
  );
}
